import React from 'react';
import { FileText, Calendar, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { formatDate } from '../../utils/helpers';

/**
 * Assignment card component for displaying an assignment in a list
 *
 * @param {Object} props
 * @param {Object} props.assignment - The assignment data
 * @param {Function} props.onSelect - Function to call when selecting an assignment
 */
const AssignmentCard = ({ assignment, onSelect }) => {
    return (
        <div
            className="group bg-white border border-gray-200 rounded-xl p-6 mb-4 shadow-md hover:shadow-lg hover:border-[#D67C2C] transition-all duration-300 cursor-pointer"
            onClick={() => onSelect(assignment)}
        >
            {/* Header: Icon + Title */}
            <div className="flex items-start gap-4">
                <div className="flex-shrink-0 h-12 w-12 bg-[#D67C2C] bg-opacity-10 rounded-full flex items-center justify-center">
                    <FileText className="h-6 w-6 text-[#D67C2C]"/>
                </div>

                <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-[#D67C2C] transition-colors">
                        {assignment.title || "Activity"}
                    </h4>
                    <p className="text-sm text-gray-600 font-medium flex items-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {assignment.domain} Domain
                        </span>
                    </p>
                </div>

                <ChevronRight size={20} className="text-gray-400 mt-2 group-hover:text-[#D67C2C] transition-colors"/>
            </div>

            {/* Activity Markdown Preview */}
            <div className="mt-4 text-sm text-gray-700 line-clamp-3">
                <ReactMarkdown
                    components={{
                        h1: ({node, ...props}) => <p className="font-semibold text-gray-900" {...props} />,
                        h2: ({node, ...props}) => <p className="font-semibold text-gray-900" {...props} />,
                        h3: ({node, ...props}) => <p className="font-semibold text-gray-900" {...props} />,
                        p: ({node, ...props}) => <p className="text-gray-700" {...props} />,
                        strong: ({node, ...props}) => <strong className="font-medium" {...props} />,
                    }}
                >
                    {assignment.activity || assignment.description || "No description provided"}
                </ReactMarkdown>
            </div>

            {/* Due Date */}
            <div className="mt-4 flex items-center text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                <Calendar className="mr-2 h-4 w-4 text-gray-500"/>
                <span className="font-medium">Due: {assignment.due_date ? formatDate(assignment.due_date) : "No due date"}</span>
            </div>
        </div>
    );
};

export default AssignmentCard;